import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Menu, MenuItem } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { clearStoredAuthTokens } from '../utils/auth';
import { clearProjectCache } from '../services/projectService';
import { useAssistantPanel } from './assistantPanelContext';
import ThemeModeToggle from './ThemeModeToggle';
import { dsVar } from '../theme/tokens';

interface AppHeaderProps {
    /**
     * Left-hand content: a page title, a back button, whatever the page needs. Pages
     * differ here; the right-hand controls do not.
     */
    left?: React.ReactNode;
    /**
     * Render the top search field with this placeholder. Omit to leave the left side to
     * `left` alone — the field is presentational (no handler is wired to it anywhere it
     * currently appears), so a page with its own working search should not show it.
     */
    searchPlaceholder?: string;
}

/**
 * The application top bar: notifications, and the account menu with sign-out.
 *
 * Extracted from the copy that was duplicated verbatim in OperatorsMarketplace,
 * LinkedDataApps and IntelligenceHub — byte-identical in all three but for the search
 * placeholder. Those three still hold their own copies; anything new should use this one
 * rather than adding a fourth.
 */
const AppHeader: React.FC<AppHeaderProps> = ({ left, searchPlaceholder }) => {
    const navigate = useNavigate();
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
    const assistant = useAssistantPanel();

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setUserMenuAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => setUserMenuAnchorEl(null);

    const handleSignOut = () => {
        clearStoredAuthTokens();
        // Not covered by clearStoredAuthTokens, which only removes keys naming
        // a token — the next user would otherwise land on this one's project.
        clearProjectCache();
        handleUserMenuClose();
        navigate('/login');
    };

    return (
        <header className="h-16 border-b border-line/50 bg-page/80 backdrop-blur-md px-8 flex items-center justify-between sticky top-0 z-10 m-0">
            <div className="flex-1 max-w-2xl text-left flex items-center gap-3">
                {searchPlaceholder ? (
                    <div className="relative group flex items-center justify-start h-full w-full">
                        <SearchIcon className="absolute left-3 text-fg-muted group-focus-within:text-accent-fg transition-colors" />
                        <input
                            className="w-full bg-surface border-none rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-1 focus:ring-accent-fg placeholder:text-fg-muted text-fg outline-none"
                            placeholder={searchPlaceholder}
                            type="text"
                        />
                    </div>
                ) : left}
            </div>

            <div className="flex items-center gap-4 ml-8">
                {assistant && (
                    <button
                        onClick={assistant.toggle}
                        aria-pressed={assistant.open}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition-colors ${
                            assistant.open
                                ? 'bg-accent/10 border-accent-fg/50 text-accent-fg'
                                : 'bg-transparent border-line text-fg-soft hover:border-accent-fg/50 hover:text-accent-fg'
                        }`}
                    >
                        <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                        {assistant.label}
                    </button>
                )}
                <button
                    className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
                    aria-label="Notifications"
                >
                    <NotificationsIcon />
                </button>
                <div
                    className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
                    onClick={handleUserMenuOpen}
                    aria-label="Open user menu"
                >
                    <AccountCircleIcon />
                </div>
                <Menu
                    anchorEl={userMenuAnchorEl}
                    open={Boolean(userMenuAnchorEl)}
                    onClose={handleUserMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            backgroundColor: dsVar('surface'),
                            color: dsVar('fg-body'),
                            border: `1px solid ${dsVar('line')}`,
                            minWidth: '180px',
                            mt: 1
                        }
                    }}
                >
                    <MenuItem
                        disabled
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: dsVar('fg-2'),
                            borderBottom: `1px solid ${dsVar('line')}`,
                            opacity: '1 !important'
                        }}
                    >
                        {localStorage.getItem('org_id') || 'User'}
                    </MenuItem>
                    <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${dsVar('line')}` }}>
                        <ThemeModeToggle />
                    </Box>
                    <MenuItem
                        onClick={() => {
                            handleUserMenuClose();
                            navigate('/access-control');
                        }}
                        sx={{
                            display: 'flex',
                            gap: 1,
                            fontSize: '0.9rem',
                            color: dsVar('fg-body'),
                            '&:hover': { backgroundColor: dsVar('fg', 0.08) }
                        }}
                    >
                        <AccountCircleIcon fontSize="small" />
                        Your account
                    </MenuItem>
                    <MenuItem
                        onClick={handleSignOut}
                        sx={{
                            display: 'flex',
                            gap: 1,
                            fontSize: '0.9rem',
                            color: dsVar('accent-fg'),
                            '&:hover': { backgroundColor: dsVar('accent', 0.08) }
                        }}
                    >
                        <LogoutIcon fontSize="small" />
                        Sign out
                    </MenuItem>
                </Menu>
            </div>
        </header>
    );
};

export default AppHeader;
